import { Circle, Group, Line } from "react-konva";
import type Konva from "konva";
import type { KonvaEventObject } from "konva/lib/Node";
import { useRef, useState } from "react";
import type { Furniture, PlacedModule, Vec2 } from "../../lib/types";
import type { FitInfo } from "../../lib/useFitToRoom";
import { placedModuleOutline } from "../../lib/furniture/modules";
import { applyModules, freePorts, moveModule } from "../../lib/furniture/modularEdit";
import { dragsImmediately, TOUCH_DRAG_DISTANCE } from "../../lib/touchPickup";
import { pressedTouch, startNodePickup, useTouchPickup } from "../../lib/useTouchPickup";
import { useCoarsePointer } from "../../lib/useViewportSize";
import { FURNITURE_SELECTED_STROKE } from "./FurnitureNode";

export interface ModuleEditorLayerProps {
  furniture: Furniture;
  modules: PlacedModule[];
  fit: FitInfo;
  stageScale: number;
  selectedModuleId: string | null;
  onSelectModule: (id: string | null) => void;
  onCommit: (patch: Partial<Furniture>) => void;
}

/**
 * Canvas overlay for "Edit modules" mode. Each module gets a draggable hit
 * outline; dragging shows a dashed ghost that snaps onto the nearest free
 * port (magnetic), and dropping commits the new arrangement. Free ports are
 * drawn as small dots so the user can see where things can attach.
 *
 * The underlying piece keeps rendering through `FurnitureNode` from the
 * store — only the ghost lives here — so nothing jumps mid-drag.
 */
export function ModuleEditorLayer({
  furniture: f,
  modules,
  fit,
  stageScale,
  selectedModuleId,
  onSelectModule,
  onCommit,
}: ModuleEditorLayerProps) {
  const inv = 1 / Math.max(stageScale, 1e-6);
  const pxPerIn = fit.scale;
  const center = fit.pointToPx({ x: f.x, y: f.y });
  const [ghost, setGhost] = useState<{ modules: PlacedModule[]; id: string; snapped: boolean } | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const grab = useRef<{ offX: number; offY: number } | null>(null);
  const coarse = useCoarsePointer();
  const pickup = useTouchPickup();

  const ports = freePorts(ghost ? ghost.modules : modules);

  /** Pointer in the piece's local frame, inches. */
  function localPointerIn(e: KonvaEventObject<DragEvent | MouseEvent>): Vec2 | null {
    const stage = e.target.getStage();
    const group = e.target.getParent()?.getParent() as Konva.Group | null; // scaled group → root group
    if (!stage || !group) return null;
    const pointer = stage.getPointerPosition();
    if (!pointer) return null;
    const t = group.getAbsoluteTransform().copy().invert();
    const p = t.point(pointer);
    return { x: p.x / pxPerIn, y: p.y / pxPerIn };
  }

  function flat(outline: Vec2[]): number[] {
    const out: number[] = [];
    for (const p of outline) out.push(p.x, p.y);
    return out;
  }

  return (
    <Group x={center.x} y={center.y} rotation={f.rotation} name="module-editor">
      <Group scaleX={pxPerIn} scaleY={pxPerIn}>
        {/* Free ports. */}
        {ports.map((p, i) => (
          <Circle
            key={`port-${i}`}
            x={p.x}
            y={p.y}
            radius={(4.5 * inv) / pxPerIn}
            fill="#ffffff"
            stroke={FURNITURE_SELECTED_STROKE}
            strokeWidth={1.5}
            strokeScaleEnabled={false}
            opacity={0.9}
            listening={false}
          />
        ))}
        {/* Module hit outlines. */}
        {modules.map((m) => {
          const outline = placedModuleOutline(m);
          const isSel = m.id === selectedModuleId;
          const isHover = m.id === hoverId;
          const isGhosted = ghost?.id === m.id;
          const immediate = dragsImmediately({ coarse, selected: isSel });
          return (
            <Line
              key={m.id}
              points={flat(outline)}
              closed
              fill={isSel ? "rgba(42,112,245,0.10)" : isHover ? "rgba(42,112,245,0.06)" : "rgba(0,0,0,0.001)"}
              stroke={isSel || isHover ? FURNITURE_SELECTED_STROKE : "rgba(42,112,245,0.35)"}
              strokeWidth={isSel ? 2 : 1}
              strokeScaleEnabled={false}
              dash={isGhosted ? [4, 3] : undefined}
              opacity={isGhosted ? 0.35 : 1}
              draggable={immediate || pickup.liftedKey === m.id}
              dragDistance={coarse ? TOUCH_DRAG_DISTANCE : undefined}
              onTouchStart={(e) => {
                if (immediate) return;
                const touch = pressedTouch(e);
                if (!touch) return;
                const node = e.currentTarget;
                const evt = e.evt;
                pickup.arm(m.id, touch, {
                  onPickup: () => {
                    onSelectModule(m.id);
                    startNodePickup(node, touch, evt);
                  },
                });
              }}
              onMouseEnter={(e) => {
                setHoverId(m.id);
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = "grab";
              }}
              onMouseLeave={(e) => {
                setHoverId((h) => (h === m.id ? null : h));
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = "default";
              }}
              onMouseDown={(e) => {
                e.cancelBubble = true;
                onSelectModule(m.id);
              }}
              onTap={(e) => {
                e.cancelBubble = true;
                onSelectModule(m.id);
              }}
              onDragStart={(e) => {
                e.cancelBubble = true;
                const lp = localPointerIn(e);
                grab.current = lp ? { offX: lp.x - m.x, offY: lp.y - m.y } : { offX: 0, offY: 0 };
                setGhost({ modules, id: m.id, snapped: true });
              }}
              onDragMove={(e) => {
                e.cancelBubble = true;
                // Keep the hit outline pinned; the ghost shows the candidate.
                e.target.position({ x: 0, y: 0 });
                const lp = localPointerIn(e);
                if (!lp) return;
                const desired = { x: lp.x - (grab.current?.offX ?? 0), y: lp.y - (grab.current?.offY ?? 0) };
                const res = moveModule(modules, m.id, desired);
                setGhost({ modules: res.modules, id: m.id, snapped: res.snapped });
              }}
              onDragEnd={(e) => {
                e.cancelBubble = true;
                e.target.position({ x: 0, y: 0 });
                const g = ghost;
                setGhost(null);
                grab.current = null;
                pickup.drop();
                // No native event = stopped from code by a pinch: drop the move.
                if (!g || !e.evt) return;
                // Always land connected: if the user let go far away, snap to
                // the nearest valid port from where the ghost was.
                const dropped = g.modules.find((x) => x.id === m.id);
                if (!dropped) return;
                const final = g.snapped
                  ? g.modules
                  : moveModule(modules, m.id, { x: dropped.x, y: dropped.y }, { forceSnap: true }).modules;
                onCommit(applyModules(f, final));
              }}
            />
          );
        })}
        {/* Drag ghost. */}
        {ghost &&
          (() => {
            const gm = ghost.modules.find((x) => x.id === ghost.id);
            if (!gm) return null;
            return (
              <Line
                points={flat(placedModuleOutline(gm))}
                closed
                fill={ghost.snapped ? "rgba(42,112,245,0.22)" : "rgba(244,63,94,0.15)"}
                stroke={ghost.snapped ? FURNITURE_SELECTED_STROKE : "#f43f5e"}
                strokeWidth={2}
                strokeScaleEnabled={false}
                dash={[6, 4]}
                listening={false}
              />
            );
          })()}
      </Group>
    </Group>
  );
}
